import { useEffect } from 'react';
import Footer from '../../home/components/Footer';

export default function WholeHomeRemodelingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://americandreamremodelinc.com';

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Whole Home Remodeling',
      description: 'Complete whole home remodeling in Southeast Michigan — full design, floor plan changes, kitchen & bath renovation, electrical, HVAC, and interior finishes by American Dream Remodeling INC.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'American Dream Remodeling INC',
        url: siteUrl,
        telephone: '+1-586-443-0294',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Detroit',
          addressRegion: 'MI',
          addressCountry: 'US',
        },
      },
      areaServed: [
        { '@type': 'County', name: 'Macomb County' },
        { '@type': 'County', name: 'Oakland County' },
        { '@type': 'County', name: 'Wayne County' },
        { '@type': 'County', name: 'Washtenaw County' },
      ],
      url: `${siteUrl}/services/whole-home-remodeling`,
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
        { '@type': 'ListItem', position: 3, name: 'Whole Home Remodeling', item: `${siteUrl}/services/whole-home-remodeling` },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does a whole home remodel cost in Southeast Michigan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Whole home remodeling costs vary widely based on the size of the home, scope of work, and materials selected. American Dream Remodeling INC provides free, no-obligation estimates for all whole home remodeling projects across Macomb, Oakland, Wayne, and Washtenaw County.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does a whole home remodel take?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A whole home remodel typically takes 2–6 months depending on the scope. We work in coordinated phases to minimize disruption and provide a detailed project timeline during your free consultation.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you manage all trades during a whole home remodel?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, American Dream Remodeling INC serves as the general contractor and manages all trades — including carpentry, electrical, plumbing, HVAC, and finishing — ensuring seamless coordination throughout your whole home remodel.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can we live in our home during a whole home remodel?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'In many cases, yes. We work in phases to keep portions of your home livable. We discuss the best approach for your specific project during the planning stage to minimize disruption to your daily life.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Whole Home Remodeling in Southeast Michigan | American Dream Remodeling INC',
      description: 'Complete whole home remodeling in Southeast Michigan — full design, floor plan changes, kitchen & bath renovation, electrical, HVAC, and interior finishes by American Dream Remodeling INC.',
      url: `${siteUrl}/services/whole-home-remodeling`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/#services` },
          { '@type': 'ListItem', position: 3, name: 'Whole Home Remodeling', item: `${siteUrl}/services/whole-home-remodeling` },
        ],
      },
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        (el as HTMLScriptElement).type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    injectSchema('schema-wholehome-service', serviceSchema);
    injectSchema('schema-wholehome-breadcrumb', breadcrumb);
    injectSchema('schema-wholehome-faq', faqSchema);
    injectSchema('schema-wholehome-webpage', webPageSchema);

    return () => {
      ['schema-wholehome-service', 'schema-wholehome-breadcrumb', 'schema-wholehome-faq', 'schema-wholehome-webpage'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const scrollToEstimate = () => {
    sessionStorage.setItem('scrollToEstimate', 'true');
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9d237554ae41cc0c7b764aa221b6fd46.jpg"
            alt="Whole Home Remodeling Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Whole Home Remodeling</h1>
          <p className="text-xl md:text-2xl mb-8">Complete home transformations that reimagine your entire living space with cohesive design and expert execution.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Transform Your Entire Home</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                A whole home remodel is the ultimate transformation. Whether you're updating an older home or completely reimagining your space, we coordinate every aspect of your project to create a cohesive, beautiful result that reflects your lifestyle and taste.
              </p>
              <p className="text-gray-600 leading-relaxed">
                American Dream Remodeling INC manages the entire process from design through completion, ensuring seamless coordination between all trades and consistent quality throughout your home. We make the complex process of whole home remodeling smooth and stress-free.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=modern%20open%20concept%20living%20room%20and%20kitchen%20remodel%20with%20cohesive%20design%20elegant%20finishes%20natural%20light%20clean%20simple%20background%20professional%20interior%20photography&width=800&height=600&seq=wholehome-detail-001&orientation=landscape"
                alt="Open Concept Remodel"
                className="w-full h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* What We Offer */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-pencil-ruler-2-line', title: 'Complete Design', desc: 'Cohesive design for your entire home' },
                { icon: 'ri-layout-grid-line', title: 'Floor Plan Changes', desc: 'Open up spaces and improve flow' },
                { icon: 'ri-paint-brush-line', title: 'Kitchen & Bath', desc: 'Complete renovation of all spaces' },
                { icon: 'ri-lightbulb-line', title: 'Electrical Updates', desc: 'Modern lighting and power systems' },
                { icon: 'ri-temp-cold-line', title: 'HVAC Improvements', desc: 'Efficient heating and cooling' },
                { icon: 'ri-home-smile-2-line', title: 'Interior Finishes', desc: 'Flooring, paint, trim, and more' },
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500 text-white rounded-lg mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Process */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-12 rounded-2xl border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Vision & Planning', desc: 'Comprehensive design and budgeting' },
                { step: '2', title: 'Permits & Prep', desc: 'Handle all approvals and logistics' },
                { step: '3', title: 'Phased Construction', desc: 'Coordinated execution room by room' },
                { step: '4', title: 'Final Reveal', desc: 'Your completely transformed home' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-500 text-white rounded-full text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Home?</h2>
          <p className="text-xl text-white/90 mb-8">Get a free, no-obligation estimate for your whole home remodeling project in Southeast Michigan.</p>
          <button
            onClick={scrollToEstimate}
            className="bg-white text-teal-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Your Free Estimate
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}